import React, { useState, useEffect } from 'react';
import apiClient from '../config/axios';
import { useNavigate } from 'react-router-dom';
import { logout } from '../utils/auth';
import './AdminPage.css';

const AdminPage = () => {
  const navigate = useNavigate();
  const [estates, setEstates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await apiClient.get('/api/admin/products');
      setEstates(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Failed to load products');
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type !== 'application/pdf') {
      setError('Please select a PDF file');
      return;
    }
    setError('');
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setUploading(true);

    if (!selectedProduct) {
      setError('Please select a product');
      setUploading(false);
      return;
    }

    const formData = new FormData();
    const fileInput = document.getElementById('subscriptionForm');
    
    if (!fileInput.files[0]) {
      setError('Please select a PDF file');
      setUploading(false);
      return;
    }

    formData.append('subscriptionForm', fileInput.files[0]);
    formData.append('productId', selectedProduct.id);

    try {
      await apiClient.post('/api/admin/upload-subscription-form', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      setSuccess(`Subscription form uploaded successfully for ${selectedProduct.name}`);
      setSelectedProduct(null);
      fileInput.value = '';
      fetchProducts();
    } catch (err) {
      console.error('Error uploading subscription form:', err);
      setError(err.response?.data?.error || 'Failed to upload subscription form');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (productId, productName) => {
    if (!window.confirm(`Are you sure you want to delete the subscription form for ${productName}?`)) {
      return;
    }

    try {
      await apiClient.delete(`/api/admin/delete-subscription-form/${productId}`);
      setSuccess(`Subscription form deleted successfully for ${productName}`);
      fetchProducts();
    } catch (err) {
      console.error('Error deleting subscription form:', err);
      setError(err.response?.data?.error || 'Failed to delete subscription form');
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="container">
          <h1>Admin - Upload Subscription Forms</h1>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1>Admin - Upload Subscription Forms</h1>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="btn btn-secondary" onClick={() => navigate('/cfu-dashboard')}>
              Go to CFU Dashboard
            </button>
            <button className="btn btn-danger" onClick={logout}>
              Logout
            </button>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="form-container">
          <h2>Upload Subscription Form</h2>
          <form onSubmit={handleUpload}>
            <div className="form-group">
              <label htmlFor="product">Select Product *</label>
              <select
                id="product"
                value={selectedProduct?.id || ''}
                onChange={(e) => {
                  // Find product across all estates
                  let foundProduct = null;
                  for (const estate of estates) {
                    foundProduct = estate.products.find(p => p.id === e.target.value);
                    if (foundProduct) break;
                  }
                  setSelectedProduct(foundProduct);
                  setError('');
                }}
                required
              >
                <option value="">Select a product</option>
                {estates.map(estate => (
                  <optgroup key={estate.id} label={estate.name}>
                    {estate.products.map(product => (
                      <option key={product.id} value={product.id}>
                        {product.name} {product.hasSubscriptionForm && '(Form uploaded)'}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="subscriptionForm">Subscription Form PDF *</label>
              <input
                type="file"
                id="subscriptionForm"
                name="subscriptionForm"
                accept=".pdf"
                onChange={handleFileSelect}
                required
              />
              <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                Only PDF files are allowed. Maximum file size: 10MB
              </p>
            </div>

            <button type="submit" className="btn btn-primary" disabled={uploading || !selectedProduct}>
              {uploading ? 'Uploading...' : 'Upload Subscription Form'}
            </button>
          </form>
        </div>

        <div className="table-container" style={{ marginTop: '40px' }}>
          <h2>Subscription Forms Status</h2>
          {estates.map(estate => (
            <div key={estate.id} style={{ marginBottom: '30px' }}>
              <h3 style={{ marginBottom: '10px', color: '#333' }}>{estate.name}</h3>
              <table>
                <thead>
                  <tr>
                    <th>Product Name</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {estate.products.map(product => (
                    <tr key={product.id}>
                      <td>{product.name}</td>
                      <td>
                        {product.hasSubscriptionForm ? (
                          <span className="status-badge yes">Form Uploaded</span>
                        ) : (
                          <span className="status-badge no">No Form</span>
                        )}
                      </td>
                      <td>
                        {product.hasSubscriptionForm ? (
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <a
                              href={product.subscriptionFormPath}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-secondary"
                              style={{ fontSize: '12px', padding: '4px 8px' }}
                            >
                              View Form
                            </a>
                            <button
                              className="btn btn-danger"
                              onClick={() => handleDelete(product.id, product.name)}
                              style={{ fontSize: '12px', padding: '4px 8px' }}
                            >
                              Delete
                            </button>
                          </div>
                        ) : (
                          <button
                            className="btn btn-primary"
                            onClick={() => {
                              setSelectedProduct(product);
                              document.getElementById('product').value = product.id;
                            }}
                            style={{ fontSize: '12px', padding: '4px 8px' }}
                          >
                            Upload Form
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
